// Global
import { sendGTMEvent } from '@next/third-parties/google';
import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { tv } from 'tailwind-variants';

// Local
import { SvgIcon } from 'helpers/SvgIcon';
import { GtmEvent } from 'lib/utils/gtm-utils';
import { StyleParamRecord } from 'lib/utils/style-param-utils';
import {
  CtaElements,
  CtaIconAlignments,
  CtaIcons,
  CtaStyleProperties,
  CtaVariants,
  CtaVisibility,
} from 'lib/utils/style-param-utils/modules/ctas';
import classNames from 'classnames';

export type CtaProps = {
  ctaStyle?: StyleParamRecord<CtaElements, CtaStyleProperties>;
  ctaIcon?: CtaIcons;
  ctaIconAlignment?: CtaIconAlignments;
  ctaVariant?: CtaVariants;
  ctaVisibility?: CtaVisibility;
};

export type ButtonWrapperProps = ButtonHTMLAttributes<HTMLButtonElement> &
  CtaProps &
  React.PropsWithChildren & {
    className?: string;
    gtmEvent?: GtmEvent;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    text?: string;
    disabled?: boolean;
  };

const ButtonWrapper = forwardRef<HTMLButtonElement, ButtonWrapperProps>(
  (
    { className, gtmEvent, onClick, text, children, disabled, ...props }: ButtonWrapperProps,
    ref
  ): JSX.Element | null => {
    const ctaIcon = props.ctaIcon ?? props.ctaStyle?.ctaIcon;
    const ctaVariant = props.ctaVariant ?? props.ctaStyle?.ctaVariant ?? 'primary';
    const ctaIconAlignment = props.ctaIconAlignment ?? props.ctaStyle?.ctaIconAlignment ?? 'right';

    const { base, icon } = ctaTailwindVariant({
      className: className,
      iconAlignment: ctaIconAlignment,
      variant: ctaVariant,
    });

    /*
     * EVENT HANDLERS
     */

    const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (gtmEvent) {
        sendGTMEvent({ ...gtmEvent });
      }

      if (onClick) {
        onClick(e);
      }
    };

    /*
     * RENDERING
     */

    // If no content is present, don't print
    if (!text && !children) return <></>;

    return (
      <button
        type={props.type || 'button'}
        className={classNames(base(), className)}
        onClick={handleOnClick}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        {text}
        {children}
        {ctaIcon && <SvgIcon className={icon()} icon={ctaIcon} size="md" />}
      </button>
    );
  }
);

ButtonWrapper.displayName = 'ButtonWrapper';

export default ButtonWrapper;

export const ctaTailwindVariant = tv({
  slots: {
    base: [],
    icon: [],
  },
  variants: {
    iconAlignment: {
      left: {
        base: ['flex-row-reverse'],
      },
      right: {
        base: ['flex-row'],
      },
    },
    variant: {
      link: {
        base: ['border-0', 'h-fit', 'w-fit'],
      },
      primary: {
        base: [
          'bg-theme-primary-btn',
          'border-theme-primary-btn-border',
          'flex',
          'font-bold',
          'font-centuryGothic',
          'gap-x-5',
          'items-center',
          'justify-center',
          'px-12',
          'py-7',
          '!text-18',
          '!leading-25',
          'rounded-sm',
          'text-center',
          'text-theme-primary-btn-text',
          'active:bg-theme-primary-btn-hover',
          'active:text-theme-primary-btn-hover-text',
          'active:border-theme-primary-btn-hover-border',
          'disabled:bg-theme-primary-btn-disabled',
          'disabled:border-theme-primary-btn-disabled',
          'disabled:no-underline',
          'disabled:text-theme-primary-btn-disabled-text',
          'focus:outline-offset-2',
          'focus:bg-theme-primary-btn',
          'focus:outline-2',
          'focus:outline-theme-primary-btn',
          'focus:border-theme-primary-btn-border',
          'focus:outline',
          'focus:text-theme-primary-text',
          'hover:bg-theme-primary-btn-hover',
          'hover:text-theme-primary-btn-hover-text',
          'hover:no-underline',
          'hover:border-theme-primary-btn-hover-border',
        ],
      },
      secondary: {
        base: [
          'bg-theme-secondary-btn',
          'border-theme-secondary-btn-border',
          'border-2',
          'flex',
          'font-bold',
          'font-centuryGothic',
          'gap-x-2',
          'items-center',
          'justify-center',
          'leading-normal',
          'px-12',
          'py-7',
          '!text-18',
          '!leading-25',
          'rounded-sm',
          'text-theme-secondary-btn-text',
          'text-center',
          'active:bg-theme-secondary-btn-hover',
          'active:border-theme-secondary-btn-hover-border',
          'active:text-theme-secondary-btn-hover-text',
          'disabled:theme-secondary-btn-disabled',
          'disabled:no-underline',
          'disabled:text-theme-secondary-btn-disabled-text',
          'disabled:border-theme-secondary-btn-disabled-border',
          'focus:outline-offset-2',
          // 'focus:bg-theme-secondary-btn',
          'focus:outline-2',
          'focus:outline-theme-secondary-btn-outline',
          'focus:outline',
          'focus:text-theme-secondary-btn-text',
          'focus:border-theme-secondary-btn-border',
          'hover:bg-theme-secondary-btn-hover',
          'hover:text-theme-secondary-btn-hover-text',
          'hover:border-theme-secondary-btn-hover-border',
          'hover:no-underline',
        ],
      },
      secondarySmall: {
        base: [
          '!text-14',
          'bg-theme-secondary-btn',
          'border-theme-secondary-btn-border',
          'border-2',
          'flex',
          'font-bold',
          'font-centuryGothic',
          'gap-x-2',
          'items-center',
          'justify-center',
          'leading-normal',
          'px-6',
          'py-2',
          'rounded-sm',
          'text-theme-secondary-btn-text',
          'text-center',
          'active:bg-theme-secondary-btn-hover',
          'active:border-theme-secondary-btn-hover-border',
          'active:text-theme-secondary-btn-hover-text',
          'disabled:theme-secondary-btn-disabled',
          'disabled:no-underline',
          'disabled:theme-secondary-btn-disabled-text',
          'disabled:border-theme-secondary-btn-disabled-border',
          'focus:outline-offset-2',
          'focus:bg-theme-secondary-btn',
          'focus:outline-2',
          'focus:outline-theme-secondary-btn-outline',
          'focus:outline',
          'focus:text-theme-secondary-btn-text',
          'focus:border-theme-secondary-btn-border',
          'hover:bg-theme-secondary-btn-hover',
          'hover:text-theme-secondary-btn-hover-text',
          'hover:border-theme-secondary-btn-hover-border',
          'hover:no-underline',
        ],
      },
      tertiary: {
        base: [
          'theme-text-btn',
          'border-theme-text-btn-border',
          'lg:flex',
          'inline',
          'font-bold',
          'font-centuryGothic',
          'gap-x-4',
          'items-center',
          'justify-center',
          'leading-normal',
          'py-7',
          '!text-18',
          '!leading-25',
          'rounded-sm',
          'lg:[&>svg]:inline-block',
          '[&>svg]:inline',
          'lg:[&>svg]:ml-0',
          '[&>svg]:ml-[10px]',
          'lg:text-center',
          'text-left',
          'text-theme-text-btn-text',
          'active:bg-theme-text-btn-hover',
          'active:text-theme-text-btn-hover-text',
          'active:border-theme-text-btn-hover-border',
          'disabled:border-theme-text-btn-disabled',
          'disabled:no-underline',
          'disabled:text-theme-text-btn-disabled-text',
          'focus:outline-offset-2',
          'focus:outline-2',
          'focus:outline-theme-text-btn-outline',
          'focus:border-theme-text-btn-border',
          'focus:outline',
          'hover:text-theme-text-btn-hover-text',
          'hover:underline',
          'hover:border-theme-text-btn-hover-border',
        ],
      },
      hyperlink: {
        base: [
          'theme-text-btn',
          'border-theme-text-btn-border',
          'flex',
          'underline',
          'font-medium',
          'font-centuryGothic',
          'gap-x-4',
          'items-start',
          'justify-start',
          '!text-16',
          'leading-25',
          'rounded-sm',
          'text-left',
          'text-theme-text-btn-text',
          'active:bg-theme-text-btn-hover',
          'active:text-theme-text-btn-hover-text',
          'active:border-theme-text-btn-hover-border',
          'disabled:border-theme-text-btn-disabled',
          'disabled:no-underline',
          'disabled:text-theme-text-btn-disabled-text',
          'focus:outline-offset-2',
          'focus:outline-2',
          'focus:outline-theme-text-btn-outline',
          'focus:border-theme-text-btn-border',
          'focus:outline',
          'hover:text-theme-text-btn-hover-text',
          'hover:underline',
          'hover:border-theme-text-btn-hover-border',
        ],
      },
      jumplink: {
        base: [
          'bg-theme-jumplink-btn',
          'border-theme-jumplink-btn-border',
          'flex',
          'font-bold',
          'font-centuryGothic',
          'gap-x-5',
          'items-center',
          'justify-center',
          'px-9',
          'py-6',
          '!text-18',
          '!leading-25',
          'rounded-[30px]',
          'text-center',
          'text-theme-jumplink-btn-text',
          'active:bg-theme-jumplink-btn-hover',
          'active:text-theme-jumplink-btn-hover-text',
          'active:border-theme-jumplink-btn-hover-border',
          'disabled:bg-theme-jumplink-btn-disabled',
          'disabled:border-theme-jumplink-btn-disabled',
          'disabled:no-underline',
          'disabled:text-theme-jumplink-btn-disabled-text',
          'focus:outline-offset-2',
          'focus:bg-theme-jumplink-btn',
          'focus:outline-2',
          'focus:outline-theme-jumplink-btn',
          'focus:border-theme-jumplink-btn-border',
          'focus:outline',
          'focus:text-theme-jumplink-btn-focus-text',
          'hover:bg-theme-jumplink-btn-hover',
          'hover:text-theme-jumplink-btn-hover-text',
          'hover:no-underline',
          'hover:border-theme-jumplink-btn-hover-border',
          'border-2',
        ],
      },
    },

    visibility: {
      visible: {
        base: [],
      },
      hidden: {
        base: ['hidden'],
      },
    },
  },
});
